import React, { useEffect, useMemo, useRef, useState } from "react";
import { useProjectContext } from "../layout/ProjectContext";
import EmptyHint from "../components/EmptyHint";
import {
  useGetDashboardQuery,
  useGetDashboardEarliestDateQuery,
  useGetPiezometerContourQuery,
  useGetBuildingSettlementContourQuery,
  useGetGroundSettlementContourQuery,
  useGetUtilitySettlementContourQuery,
  useGetMovementSettlementContourQuery,
  useGetActivitiesQuery,
  useGetAlertProfilesQuery,
  type AlertProfile,
  type DashboardDevice,
} from "../features/dashboard/apiSlice";
import { useGetSiteLayoutQuery } from "../features/management-dashboard/apiSlice";
import {
  useGetDevicesByProjectQuery,
  useGetCheckpointNamesByTypeQuery,
} from "../features/devices/apiSlice";
import {
  CHART_TYPES,
  getTypeConfig,
  buildDeviceMeta,
  buildDeviceSeries,
  buildGroupedColumn,
  isSettlementKey,
  unitFor,
  type MetricMode,
  type CrackAxis,
  type GroupedColumn,
} from "../features/dashboard/chartUtils";
import LineChart, {
  type AlertToDisplay,
  type NamedSeries,
} from "../features/dashboard/components/LineChart";
import DataTable from "../features/dashboard/components/DataTable";
import ContourPlot, {
  type ContourPoint,
} from "../features/dashboard/components/ContourPlot";
import type { CalibrationPoint } from "../features/site-layout/types";
import DistortionView from "../features/dashboard/components/DistortionView";
import { isMovementType } from "../utils/movementTypes";
import dayjs from "dayjs";

const EMPTY_CHECKPOINTS: string[] = [];

type View = "charts" | "contour" | "table";

const SLOPE_RATIO_PLOT_MAX = 2000;
const CAPPED_RATIO_NOTE =
  "Values plotted at 2000 mean the actual value may be greater than 2000.";

// Build the alert-line mapper for a given device type + metric mode.
// Returning undefined hides alert lines for that combination.
function alertMapperFor(
  typeKey: string,
  mode: MetricMode,
): AlertToDisplay | undefined {
  if (typeKey === "ground_water") {
    if (mode !== "reading") return undefined; // elevation (mPD) only
    return (level, levels) => {
      const lowest = levels.find((l) => l.name === "Lowest")?.value;
      const highest = levels.find((l) => l.name === "Highest")?.value;
      if (levels.length === 4 && lowest != null && highest != null) {
        if (level.name === "Level above the lowest")
          return lowest + level.value;
        if (level.name === "Level below the highest")
          return highest - level.value;
      }
      return level.value;
    };
  }
  if (
    typeKey === "ground_settlement" ||
    typeKey === "building_settlement" ||
    typeKey === "utility_settlement"
  ) {
    if (mode !== "change") return undefined;
    return (level) => -Math.abs(level.value); // settlement action level is mm and -ve
  }
  if (typeKey === "crack") {
    if (mode !== "change") return undefined;
    return (level) => level.value; // mm
  }
  if (typeKey === "tilting") {
    if (mode !== "change") return undefined;
    return (level) =>
      level.entry_mode === "gradient" && level.value > 0
        ? Math.min(SLOPE_RATIO_PLOT_MAX, Math.round(1 / level.value))
        : null;
  }
  if (typeKey === "vibration") {
    return (level) => level.value; // mm/s, group resolved in LineChart
  }
  if (isMovementType(typeKey)) {
    // Applies to both settlement (plotted -ve) and distance (plotted +ve);
    // LineChart mirrors the line at ±value.
    if (mode !== "change") return undefined;
    return (level) => Math.abs(level.value); // mm
  }
  return undefined;
}

function numberFormatFor(typeKey: string): (v: number) => string {
  if (typeKey === "tilting") return (v) => String(Math.round(v));
  if (typeKey === "ground_water" || typeKey === "vibration")
    return (v) => v.toFixed(3);
  return (v) => v.toFixed(2);
}

// Slope-ratio formatter (tilting / distortion). Beyond 1:2000 the change is
// effectively flat, so collapse it to a single "< 1 in 2000" label.
function formatSlope(v: number): string {
  const rounded = Math.round(v);
  if (Math.abs(rounded) > 2000) return "< 1 in 2000";
  return String(rounded);
}

// Settlement Diff (mm) — trims trailing zeros, treats sub-precision residuals
// as zero to avoid scientific-notation artefacts.
function formatSettlementDiff(v: number): string {
  if (!Number.isFinite(v) || Math.abs(v) < 1e-4) return "0";
  const intDigits = Math.floor(Math.log10(Math.abs(v))) + 1;
  const dec = Math.max(4, 4 - intDigits);
  return String(+v.toFixed(dec));
}

interface GroupedFormats {
  readingFormat: (v: number) => string;
  changeFormat: (v: number) => string;
  preExistingFormat: (v: number) => string;
}

function groupedFormatsFor(typeKey: string): GroupedFormats {
  if (isMovementType(typeKey)) {
    return {
      readingFormat: (v) => v.toFixed(3),
      changeFormat: formatSettlementDiff,
      preExistingFormat: (v) => v.toFixed(1),
    };
  }
  if (typeKey === "tilting") {
    return {
      readingFormat: formatSlope,
      changeFormat: formatSlope,
      preExistingFormat: formatSlope,
    };
  }
  if (isSettlementKey(typeKey)) {
    return {
      readingFormat: (v) => v.toFixed(3),
      changeFormat: formatSettlementDiff,
      preExistingFormat: (v) => v.toFixed(1),
    };
  }
  if (typeKey === "ground_water") {
    return {
      readingFormat: (v) => v.toFixed(3),
      changeFormat: (v) => v.toFixed(3),
      preExistingFormat: (v) => v.toFixed(3),
    };
  }
  // crack
  return {
    readingFormat: (v) => v.toFixed(2),
    changeFormat: (v) => v.toFixed(2),
    preExistingFormat: (v) => v.toFixed(2),
  };
}

// Chart types that combine several device categories on a single contour.
const COMBINED_CONTOUR_TYPES: Record<string, string[]> = {
  ground_utility_settlement: ["ground_settlement", "utility_settlement"],
};

// Display label for an "xy" sub-series (crack: X/Y, movement: Settlement/Distance).
function axisLabelFor(
  config: { axes?: { key: CrackAxis; label: string }[] },
  axis: CrackAxis,
): string {
  return config.axes?.find((a) => a.key === axis)?.label ?? axis.toUpperCase();
}

function hasSeriesAboveMax(series: NamedSeries[], max: number): boolean {
  return series.some((s) => s.points.some((p) => p.value > max));
}

function clampSeriesMax(series: NamedSeries[], max: number): NamedSeries[] {
  return series.map((s) => ({
    ...s,
    points: s.points.map((p) => ({
      ...p,
      value: p.value > max ? max : p.value,
    })),
  }));
}

const Dashboard: React.FC = () => {
  const { projectId } = useProjectContext();

  const [activeType, setActiveType] = useState<string>("ground_movement");
  const [view, setView] = useState<View>("charts");
  const [from, setFrom] = useState<string>(
    dayjs().add(-3, "month").format("YYYY-MM-DD"),
  );

  const [to, setTo] = useState<string>(dayjs().format("YYYY-MM-DD"));
  const [singleDate, setSingleDate] = useState<string>("");
  const [selected, setSelected] = useState<Set<string>>(new Set());

  const [metricMode, setMetricMode] = useState<MetricMode>("change");
  const [crackAxes, setCrackAxes] = useState<Set<CrackAxis>>(
    new Set<CrackAxis>(["x", "y"]),
  );
  const [showDailyGridlines, setShowDailyGridlines] = useState<boolean>(false);
  const [hideActivityTimelines, setHideActivityTimelines] =
    useState<boolean>(false);
  const [checkpointModalOpen, setCheckpointModalOpen] =
    useState<boolean>(false);
  const [pendingSelected, setPendingSelected] = useState<Set<string>>(
    new Set(),
  );

  // Throttled state for API query (2 second delay)
  const [throttledFrom, setThrottledFrom] = useState<string>(
    dayjs().add(-3, "month").format("YYYY-MM-DD"),
  );
  const [throttledTo, setThrottledTo] = useState<string>(
    dayjs().format("YYYY-MM-DD"),
  );
  const [throttledSelected, setThrottledSelected] = useState<Set<string>>(
    new Set(),
  );

  const [isThrottling, setIsThrottling] = useState<boolean>(false);
  const [isInitialLoad, setIsInitialLoad] = useState<boolean>(true);
  const selectionScopeRef = useRef<string>("");
  const fromScopeRef = useRef<string>("");

  // Throttle from, to, and selected with 2 second delay
  useEffect(() => {
    // On initial load or when values are empty, update immediately without throttling
    if (isInitialLoad || (from === "" && to === "" && selected.size === 0)) {
      setThrottledFrom(from);
      setThrottledTo(to);
      setThrottledSelected(new Set(selected));
      setIsInitialLoad(false);
      return;
    }

    setIsThrottling(true);
    const timer = setTimeout(() => {
      setThrottledFrom(from);
      setThrottledTo(to);
      setThrottledSelected(new Set(selected));
      setIsThrottling(false);
    }, 500);

    return () => {
      clearTimeout(timer);
    };
  }, [from, to, selected, isInitialLoad]);

  const config = getTypeConfig(activeType);

  const {
    data: earliestDateData,
    isSuccess: earliestDateReady,
    isFetching: earliestDateFetching,
  } = useGetDashboardEarliestDateQuery(
    {
      projectId: projectId ?? undefined,
      deviceType: activeType,
    },
    { skip: projectId == null },
  );

  useEffect(() => {
    if (!earliestDateReady || earliestDateFetching) return;

    const scope = `${projectId ?? "none"}:${activeType}`;
    const earliestDate = earliestDateData?.earliestDate ?? "";
    const marker = `${scope}:${earliestDate || "none"}`;
    if (fromScopeRef.current === marker) return;

    if (!earliestDate) {
      setFrom("");
      setThrottledFrom("");
      fromScopeRef.current = marker;
      return;
    }

    setFrom(earliestDate);
    setThrottledFrom(earliestDate);
    fromScopeRef.current = marker;
  }, [
    earliestDateData,
    earliestDateReady,
    earliestDateFetching,
    projectId,
    activeType,
  ]);

  const throttledDeviceIds = useMemo(
    () => Array.from(throttledSelected),
    [throttledSelected],
  );

  const { data: dashData, isFetching: dashLoading } = useGetDashboardQuery(
    {
      projectId: projectId ?? undefined,
      startDate: throttledFrom || undefined,
      endDate: throttledTo || undefined,
      deviceNames:
        throttledDeviceIds.length > 0 ? throttledDeviceIds : undefined,
    },
    { skip: projectId == null || throttledDeviceIds.length === 0 },
  );

  const { data: metaDevices = [] } = useGetDevicesByProjectQuery(
    projectId as number,
    { skip: projectId == null },
  );
  const { data: activities = [] } = useGetActivitiesQuery(
    {
      projectId: projectId as number,
      from: throttledFrom || undefined,
      to: throttledTo || undefined,
    },
    { skip: projectId == null },
  );

  const { data: alertProfiles = [] } = useGetAlertProfilesQuery(
    { projectId: projectId as number },
    { skip: projectId == null },
  );

  const profileMap = useMemo(() => {
    const m = new Map<number, AlertProfile>();
    alertProfiles.forEach((p) => m.set(p.id, p));
    return m;
  }, [alertProfiles]);

  const deviceMeta = useMemo(() => buildDeviceMeta(metaDevices), [metaDevices]);

  // Fetch available checkpoint names for the current type
  const {
    data: checkpointNames = EMPTY_CHECKPOINTS,
    isSuccess: checkpointNamesReady,
    isFetching: checkpointNamesFetching,
  } = useGetCheckpointNamesByTypeQuery(
    {
      deviceType: activeType,
      projectId: projectId ?? undefined,
    },
    { skip: projectId == null },
  );

  // Create device objects for the checkpoint modal from checkpoint names
  const availableDevices: DashboardDevice[] = useMemo(() => {
    return checkpointNames.map((name) => ({
      deviceId: name,
      deviceName: name,
      deviceType: activeType,
      readings: [],
      location: { northing: 0, easting: 0 },
    }));
  }, [checkpointNames, activeType]);

  // Get actual dashboard data with readings (filtered by type)
  const devices: DashboardDevice[] = useMemo(() => {
    const members = COMBINED_CONTOUR_TYPES[activeType] ?? [activeType];
    return (dashData?.devices ?? []).filter((d) =>
      members.includes(d.deviceType),
    );
  }, [dashData, activeType]);

  useEffect(() => {
    if (!checkpointNamesReady || checkpointNamesFetching) return;

    const scope = `${projectId ?? "none"}:${activeType}`;

    if (selectionScopeRef.current !== scope) {
      const nextSet = new Set(checkpointNames);
      setSelected(nextSet);
      setThrottledSelected(new Set(nextSet));
      selectionScopeRef.current = scope;
      return;
    }

    setSelected((prev) => {
      const nextSet = new Set(
        Array.from(prev).filter((name) => checkpointNames.includes(name)),
      );

      if (nextSet.size !== prev.size) {
        setThrottledSelected(new Set(nextSet));
        return nextSet;
      }

      return prev;
    });
  }, [
    checkpointNames,
    checkpointNamesReady,
    checkpointNamesFetching,
    projectId,
    activeType,
  ]);

  // Force a valid metric mode when switching to a type that lacks "change".
  useEffect(() => {
    if (!config.hasChange) setMetricMode("reading");
    else if (!config.hasReading) setMetricMode("change");
  }, [config.hasChange, config.hasReading]);

  // Contour-only types have no line chart / data table — force the contour view.
  useEffect(() => {
    if (config.contourOnly) setView("contour");
  }, [config.contourOnly, activeType]);

  const visibleDevices = useMemo(
    () => devices.filter((d) => selected.has(d.deviceName)),
    [devices, selected],
  );

  const effectiveMode: MetricMode = config.lineModes.includes(metricMode)
    ? metricMode
    : config.lineModes[0];

  const series: NamedSeries[] = useMemo(() => {
    const inRange = (date: string) => {
      const day = dayjs(date).format("YYYY-MM-DD");
      return (!from || day >= from) && (!to || day <= to);
    };
    const out: NamedSeries[] = [];
    visibleDevices.forEach((d) => {
      const meta = deviceMeta.get(d.deviceName);
      const profileId = meta?.alertProfileId ?? null;
      if (config.series === "xy") {
        (["x", "y"] as CrackAxis[]).forEach((axis) => {
          if (!crackAxes.has(axis)) return;
          // Movement: settlement (x) uses the settlement profile, lateral
          // distance (y) uses the lateral profile.
          const axisProfileId =
            isMovementType(config.key) && axis === "y"
              ? (meta?.lateralAlertProfileId ?? null)
              : profileId;
          out.push({
            name: `${d.deviceName} ${axisLabelFor(config, axis)}`,
            deviceName: d.deviceName,
            points: buildDeviceSeries(
              d,
              config,
              effectiveMode,
              axis,
              meta,
            ).filter((p) => inRange(p.date)),
            alertProfileId: axisProfileId,
          });
        });
      } else {
        out.push({
          name: d.deviceName,
          deviceName: d.deviceName,
          points: buildDeviceSeries(d, config, effectiveMode, "x", meta).filter(
            (p) => inRange(p.date),
          ),
          alertProfileId: profileId,
        });
      }
    });
    return out;
  }, [visibleDevices, deviceMeta, config, effectiveMode, crackAxes, from, to]);

  // Grouped table columns (R.L + Diff per device) for every type except
  // settlement distortion and vibration.
  const groupedColumns: GroupedColumn[] = useMemo(() => {
    if (config.isDistortion || config.key === "vibration") return [];
    const cols: GroupedColumn[] = [];
    visibleDevices.forEach((d) => {
      const meta = deviceMeta.get(d.deviceName);
      if (config.series === "xy") {
        (["x", "y"] as CrackAxis[]).forEach((axis) => {
          if (!crackAxes.has(axis)) return;
          cols.push(
            buildGroupedColumn(
              d,
              config,
              axis,
              meta,
              `${d.deviceName} ${axisLabelFor(config, axis)}`,
              from,
              to,
            ),
          );
        });
      } else {
        cols.push(
          buildGroupedColumn(d, config, "x", meta, d.deviceName, from, to),
        );
      }
    });
    return cols;
  }, [visibleDevices, deviceMeta, config, crackAxes, from, to]);

  const toggleChip = (name: string) =>
    setPendingSelected((prev) => {
      const next = new Set(prev);
      if (next.has(name)) next.delete(name);
      else next.add(name);
      return next;
    });
  const selectAll = () =>
    setPendingSelected(new Set(availableDevices.map((d) => d.deviceName)));
  const selectNone = () => setPendingSelected(new Set());
  const openCheckpointModal = () => {
    setPendingSelected(new Set(selected));
    setCheckpointModalOpen(true);
  };
  const submitCheckpoints = () => {
    setSelected(new Set(pendingSelected));
    setCheckpointModalOpen(false);
  };
  const toggleAxis = (axis: CrackAxis) =>
    setCrackAxes((prev) => {
      const next = new Set(prev);
      if (next.has(axis) && next.size > 1) next.delete(axis);
      else next.add(axis);
      return next;
    });

  if (projectId == null) {
    return (
      <EmptyHint
        icon="bi-folder2-open"
        title="Select a project"
        subtitle="Pick a project from the top bar to load charts."
      />
    );
  }

  const isDistortion = config.isDistortion;
  const shouldCapSlopePlot = config.key === "tilting";
  const showMetricToggle =
    !isDistortion && view === "charts" && config.lineModes.length > 1;
  const unit = unitFor(config, effectiveMode);
  const yAxisLabel = `${config.label} ${
    effectiveMode === "change" ? "Change" : "Reading"
  } (${unit})`;
  const groupedFmts = groupedFormatsFor(config.key);

  const lineChartFor = (
    data: NamedSeries[],
    alertOverride?: AlertToDisplay,
    mirrorOverride?: boolean,
  ) => {
    const hasCappedValues =
      shouldCapSlopePlot && hasSeriesAboveMax(data, SLOPE_RATIO_PLOT_MAX);
    const chartSeries = shouldCapSlopePlot
      ? clampSeriesMax(data, SLOPE_RATIO_PLOT_MAX)
      : data;
    const modeSignNote =
      effectiveMode === "change" ? config.signNote : undefined;
    const chartSignNote =
      hasCappedValues && modeSignNote
        ? `${modeSignNote} ${CAPPED_RATIO_NOTE}`
        : hasCappedValues
          ? CAPPED_RATIO_NOTE
          : modeSignNote;

    return (
      <LineChart
        series={chartSeries}
        yAxisLabel={yAxisLabel}
        yAxisUnit={unit}
        hoverFormat={config.hoverFormat}
        markersOnly={config.markersOnly}
        invertY={config.invertY}
        signNote={chartSignNote}
        alertProfiles={profileMap}
        alertToDisplay={
          alertOverride !== undefined
            ? alertOverride
            : alertMapperFor(config.key, effectiveMode)
        }
        activities={hideActivityTimelines ? [] : activities}
        startDate={from}
        endDate={to}
        yAxisRange={shouldCapSlopePlot ? [0, SLOPE_RATIO_PLOT_MAX] : undefined}
        showDailyGridlines={showDailyGridlines}
        mirrorAlertLines={
          mirrorOverride !== undefined ? mirrorOverride : config.mirrorAlert
        }
      />
    );
  };

  // When series span multiple action profiles, render one chart per profile with a heading.
  const groupedChartsFor = (data: NamedSeries[]) => {
    const groups = new Map<number | null, NamedSeries[]>();
    data.forEach((s) => {
      const pid = s.alertProfileId;
      if (!groups.has(pid)) groups.set(pid, []);
      groups.get(pid)!.push(s);
    });

    if (groups.size <= 1) return lineChartFor(data);
    return (
      <>
        {Array.from(groups.entries())
          .filter((g) => g[0] !== null)
          .map(([profileId, groupSeries]) => {
            const profile =
              profileId != null ? profileMap.get(profileId) : undefined;
            const profileName = profile?.description ?? "No action profile";
            // Movement settlement → negative levels only; lateral → positive only.
            let alertOverride: AlertToDisplay | undefined;
            let mirrorOverride: boolean | undefined;
            if (isMovementType(config.key)) {
              if (profile?.profile_type === "movement_settlement") {
                alertOverride = (level) => -Math.abs(level.value);
                mirrorOverride = false;
              } else if (profile?.profile_type === "movement_lateral") {
                alertOverride = (level) => Math.abs(level.value);
                mirrorOverride = false;
              }
            }
            return (
              <div key={profileId ?? "null"} style={{ marginBottom: 32 }}>
                <div
                  className="fw-600"
                  style={{ margin: "0 0 6px", fontSize: "1rem" }}
                >
                  {config.label} — {profileName}
                </div>
                {lineChartFor(groupSeries, alertOverride, mirrorOverride)}
              </div>
            );
          })}
      </>
    );
  };

  return (
    <div className="card mb-4" style={{ position: "relative" }}>
      {/* Loading indicator bar */}
      {(dashLoading || isThrottling) && (
        <div
          style={{
            position: "absolute",
            top: 0,
            left: 0,
            right: 0,
            height: 3,
            backgroundColor: "#e9ecef",
            zIndex: 1000,
            overflow: "hidden",
          }}
        >
          <div
            style={{
              height: "100%",
              width: "50%",
              backgroundColor: "#007bff",
              animation: "loading-bar 1.5s ease-in-out infinite",
            }}
          />
        </div>
      )}
      <style>{`
        @keyframes loading-bar {
          0% {
            transform: translateX(-100%);
          }
          50% {
            transform: translateX(200%);
          }
          100% {
            transform: translateX(-100%);
          }
        }
      `}</style>

      {/* Type tabs */}
      <div className="tabs">
        {CHART_TYPES.filter((t) => t.key !== "movement").map((t) => (
          <button
            key={t.key}
            className={t.key === activeType ? "active" : undefined}
            onClick={() => {
              setActiveType(t.key);
              if (!t.hasContour && view === "contour") setView("charts");
            }}
          >
            {t.label}
          </button>
        ))}
      </div>

      {/* Filter bar */}
      <div className="filter-bar">
        <div className="field">
          <label>View</label>
          <div className="view-switch">
            {!config.contourOnly && (
              <button
                className={view === "charts" ? "active" : undefined}
                onClick={() => setView("charts")}
              >
                <i className="bi bi-graph-up" /> Line Chart
              </button>
            )}
            {config.hasContour && (
              <button
                className={view === "contour" ? "active" : undefined}
                onClick={() => setView("contour")}
              >
                <i className="bi bi-bullseye" /> Contour
              </button>
            )}
            {!config.contourOnly && (
              <button
                className={view === "table" ? "active" : undefined}
                onClick={() => setView("table")}
              >
                <i className="bi bi-table" /> Data Table
              </button>
            )}
          </div>
        </div>

        {showMetricToggle && (
          <div className="field">
            <label>Metric</label>
            <div className="view-switch">
              <button
                className={metricMode === "change" ? "active" : undefined}
                onClick={() => setMetricMode("change")}
              >
                Change ({config.changeUnit})
              </button>
              <button
                className={metricMode === "reading" ? "active" : undefined}
                onClick={() => setMetricMode("reading")}
              >
                Reading ({config.readingUnit})
              </button>
            </div>
          </div>
        )}

        {config.series === "xy" && view !== "contour" && (
          <div className="field">
            <label>{isMovementType(config.key) ? "Series" : "Axis"}</label>
            <div className="view-switch">
              {(
                config.axes ?? [
                  { key: "x" as CrackAxis, label: "X" },
                  { key: "y" as CrackAxis, label: "Y" },
                ]
              ).map((a) => (
                <button
                  key={a.key}
                  className={crackAxes.has(a.key) ? "active" : undefined}
                  onClick={() => toggleAxis(a.key)}
                >
                  {a.label}
                </button>
              ))}
            </div>
          </div>
        )}

        {view !== "contour" && (
          <>
            <div className="field">
              <label>From</label>
              <input
                className="input"
                type="date"
                value={from}
                onChange={(e) => setFrom(e.target.value)}
              />
            </div>
            <div className="field">
              <label>To</label>
              <input
                className="input"
                type="date"
                value={to}
                onChange={(e) => setTo(e.target.value)}
              />
            </div>
            {view === "charts" && (
              <>
                <div className="field">
                  <label>
                    <input
                      type="checkbox"
                      checked={showDailyGridlines}
                      onChange={(e) => setShowDailyGridlines(e.target.checked)}
                      style={{ marginRight: 6 }}
                    />
                    Daily gridlines
                  </label>
                </div>
                <div className="field">
                  <label>
                    <input
                      type="checkbox"
                      checked={hideActivityTimelines}
                      onChange={(e) =>
                        setHideActivityTimelines(e.target.checked)
                      }
                      style={{ marginRight: 6 }}
                    />
                    Hide activity timelines
                  </label>
                </div>
              </>
            )}
          </>
        )}

        {view === "contour" && (
          <div className="field">
            <label>Date</label>
            <input
              className="input"
              type="date"
              value={singleDate}
              onChange={(e) => setSingleDate(e.target.value)}
            />
          </div>
        )}

        {!isDistortion && (
          <div className="field">
            <label>Checkpoints</label>
            <button
              type="button"
              className="btn btn-outline"
              onClick={openCheckpointModal}
            >
              <i className="bi bi-list-check" style={{ marginRight: 6 }} />
              {selected.size} / {availableDevices.length} selected
            </button>
          </div>
        )}
      </div>

      {checkpointModalOpen && (
        <CheckpointModal
          devices={availableDevices}
          pending={pendingSelected}
          onToggle={toggleChip}
          onSelectAll={selectAll}
          onSelectNone={selectNone}
          onSubmit={submitCheckpoints}
          onCancel={() => setCheckpointModalOpen(false)}
        />
      )}

      <div
        className="card-body"
        style={{
          opacity: dashLoading || isThrottling ? 0.6 : 1,
          transition: "opacity 0.3s ease",
          pointerEvents: dashLoading || isThrottling ? "none" : "auto",
        }}
      >
        {isDistortion ? (
          <DistortionView
            projectId={projectId}
            view={view === "contour" ? "charts" : view}
            from={from}
            to={to}
            showDailyGridlines={showDailyGridlines}
            onDatesResolved={(start, end) => {
              if (!from) setFrom(start);
              if (!to) setTo(end);
            }}
          />
        ) : (
          <>
            {!dashLoading && !isThrottling && view === "charts" && (
              <>
                {config.key === "ground_water" ? (
                  series.length ? (
                    series.map((s) => (
                      <div key={s.name} style={{ marginBottom: 24 }}>
                        <div className="fw-600" style={{ margin: "8px 0 2px" }}>
                          {s.name}
                        </div>
                        {lineChartFor([s])}
                      </div>
                    ))
                  ) : (
                    <EmptyHint
                      icon="bi-info-circle"
                      title="No checkpoints selected"
                      subtitle="Select a ground water checkpoint to plot."
                    />
                  )
                ) : (
                  groupedChartsFor(series)
                )}
              </>
            )}
            {!dashLoading &&
              !isThrottling &&
              view === "table" &&
              (config.key === "vibration" ? (
                <DataTable
                  series={series}
                  unit={unit}
                  numberFormat={numberFormatFor(config.key)}
                />
              ) : (
                <DataTable
                  grouped={groupedColumns}
                  readingUnit={
                    isMovementType(config.key) ? "mPD / mm" : config.readingUnit
                  }
                  changeUnit={config.changeUnit}
                  readingFormat={groupedFmts.readingFormat}
                  changeFormat={groupedFmts.changeFormat}
                  preExistingFormat={groupedFmts.preExistingFormat}
                  isCrackGauge={config.key === "crack"}
                />
              ))}
            {!dashLoading &&
              !isThrottling &&
              view === "contour" &&
              config.hasContour && (
                <ContourView
                  t={config}
                  projectId={projectId}
                  singleDate={singleDate}
                  selected={selected}
                />
              )}
          </>
        )}
      </div>
    </div>
  );
};

// ===== Contour (Plotly IDW contour over site layout) =====
const CONTOUR_HOOK: Partial<
  Record<
    string,
    | "piezometer"
    | "building"
    | "ground"
    | "utility"
    | "ground_utility"
    | "movement_settlement"
  >
> = {
  movement: "movement_settlement",
  ground_movement: "movement_settlement",
  building_movement: "movement_settlement",
  utility_movement: "movement_settlement",
  ground_water: "piezometer",
  building_settlement: "building",
  ground_settlement: "ground",
  utility_settlement: "utility",
  ground_utility_settlement: "ground_utility",
};

interface ContourProps {
  t: { key: string; label: string; readingUnit: string; changeUnit: string };
  projectId: number;
  singleDate: string;
  selected: Set<string>;
}

const ContourView: React.FC<ContourProps> = ({
  t,
  projectId,
  singleDate,
  selected,
}) => {
  console.log(t);
  const which = CONTOUR_HOOK[t.key];
  const isGroundWater = which === "piezometer";

  // Ground water can plot either the water-level change (m) or the actual
  // water level (mPD); user-selectable. Settlement always plots change (mm).
  const [waterMode, setWaterMode] = useState<"change" | "level">("change");

  const piezo = useGetPiezometerContourQuery(
    { projectId, date: singleDate || undefined },
    { skip: which !== "piezometer" },
  );
  const build = useGetBuildingSettlementContourQuery(
    { projectId, date: singleDate || undefined },
    { skip: which !== "building" },
  );
  const ground = useGetGroundSettlementContourQuery(
    { projectId, date: singleDate || undefined },
    { skip: which !== "ground" && which !== "ground_utility" },
  );
  const util = useGetUtilitySettlementContourQuery(
    { projectId, date: singleDate || undefined },
    { skip: which !== "utility" && which !== "ground_utility" },
  );
  const movement = useGetMovementSettlementContourQuery(
    { projectId, date: singleDate || undefined, deviceType: t.key },
    { skip: which !== "movement_settlement" },
  );

  const { data: siteLayout } = useGetSiteLayoutQuery(projectId);

  // Must be before any early returns (Rules of Hooks)
  const calibrationPoints: CalibrationPoint[] | undefined = useMemo(() => {
    const layout = siteLayout?.layouts?.find(
      (l) => l.calibrationPoints && l.calibrationPoints.length >= 2,
    );
    if (!layout) return undefined;
    return layout.calibrationPoints.map((cp) => ({
      imageCoordinate: { x: cp.image_x, y: cp.image_y },
      realWorldCoordinate: {
        easting: cp.real_easting,
        northing: cp.real_northing,
      },
    }));
  }, [siteLayout]);

  const contourResp: any =
    which === "piezometer"
      ? piezo.data
      : which === "building"
        ? build.data
        : which === "ground"
          ? ground.data
          : which === "utility"
            ? util.data
            : which === "ground_utility"
              ? {
                  project: ground.data?.project ?? util.data?.project,
                  date: ground.data?.date ?? util.data?.date,
                  devices: [
                    ...(ground.data?.devices ?? []),
                    ...(util.data?.devices ?? []),
                  ],
                }
              : which === "movement_settlement"
                ? movement.data
                : null;

  if (!which) {
    return (
      <EmptyHint
        icon="bi-bullseye"
        title="Contour not available"
        subtitle="Contour view is only available for settlement and ground water types."
      />
    );
  }

  const isLoading =
    (which === "piezometer" && piezo.isFetching) ||
    (which === "building" && build.isFetching) ||
    (which === "ground" && ground.isFetching) ||
    (which === "utility" && util.isFetching) ||
    (which === "ground_utility" && (ground.isFetching || util.isFetching)) ||
    (which === "movement_settlement" && movement.isFetching);

  if (isLoading) {
    return (
      <EmptyHint
        icon="bi-hourglass-split"
        title="Loading…"
        subtitle="Fetching contour data."
      />
    );
  }

  const rawDevices: any[] = contourResp?.devices ?? [];
  const filtered = selected.size
    ? rawDevices.filter((d) => selected.has(d.deviceName))
    : rawDevices;

  const layoutImage =
    contourResp?.project?.layout_image_filename ||
    siteLayout?.project?.layoutImage;
  const apiBase = process.env.REACT_APP_API_URL || "/";
  const imgUrl = layoutImage ? `${apiBase}files/${layoutImage}` : null;

  // Ground water plots either the water-level change (m) or the actual water
  // level (mPD) depending on the selected mode; settlement plots cumulative
  // change (mm). Backend already provides those fields.
  const points: ContourPoint[] = filtered.flatMap((d) => {
    const zRaw = isGroundWater
      ? waterMode === "change"
        ? d.waterLevelChange
        : d.waterLevel
      : (d.settlementChange ?? d.elevationValue);
    const x = Number(d.imageX);
    const y = Number(d.imageY);
    const z = zRaw == null ? NaN : Number(zRaw);
    if (!Number.isFinite(z) || !Number.isFinite(x) || !Number.isFinite(y)) {
      return [];
    }
    return [
      {
        name: d.deviceName,
        x,
        y,
        z,
        easting: d.realEasting != null ? Number(d.realEasting) : undefined,
        northing: d.realNorthing != null ? Number(d.realNorthing) : undefined,
      },
    ];
  });

  if (!points.length) {
    return (
      <EmptyHint
        icon="bi-info-circle"
        title="No contour data"
        subtitle="No readings on the selected date for this type."
      />
    );
  }

  const displayDate = contourResp?.date || singleDate;

  // Units: ground water → change (m) or level (mPD); settlement → change (mm).
  const zUnit = isGroundWater
    ? waterMode === "change"
      ? t.changeUnit
      : t.readingUnit
    : t.changeUnit;
  const zDecimals = isGroundWater ? 3 : 2;

  return (
    <>
      {isGroundWater && (
        <div
          className="btn-group btn-group-sm mb-2"
          role="group"
          aria-label="Ground water contour value"
        >
          <button
            type="button"
            className={`btn btn-outline-primary${
              waterMode === "change" ? " active" : ""
            }`}
            onClick={() => setWaterMode("change")}
          >
            Change ({t.changeUnit})
          </button>
          <button
            type="button"
            className={`btn btn-outline-primary${
              waterMode === "level" ? " active" : ""
            }`}
            onClick={() => setWaterMode("level")}
          >
            Water Level ({t.readingUnit})
          </button>
        </div>
      )}
      <ContourPlot
        points={points}
        backgroundUrl={imgUrl}
        calibrationPoints={calibrationPoints}
        plotTitle={`${t.label} Contour${displayDate ? ` · ${displayDate}` : ""}`}
        zAxisTitle={zUnit}
        numberFormat={(v) => v.toFixed(zDecimals)}
      />
    </>
  );
};

// ===== Checkpoint selector modal =====
interface CheckpointModalProps {
  devices: DashboardDevice[];
  pending: Set<string>;
  onToggle: (name: string) => void;
  onSelectAll: () => void;
  onSelectNone: () => void;
  onSubmit: () => void;
  onCancel: () => void;
}

const CheckpointModal: React.FC<CheckpointModalProps> = ({
  devices,
  pending,
  onToggle,
  onSelectAll,
  onSelectNone,
  onSubmit,
  onCancel,
}) => (
  <div
    style={{
      position: "fixed",
      inset: 0,
      zIndex: 1050,
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
    }}
  >
    {/* Backdrop */}
    <div
      style={{
        position: "absolute",
        inset: 0,
        background: "rgba(0,0,0,0.4)",
      }}
      onClick={onCancel}
    />
    {/* Dialog */}
    <div
      style={{
        position: "relative",
        background: "#fff",
        borderRadius: 8,
        boxShadow: "0 8px 32px rgba(0,0,0,0.18)",
        width: "min(560px, 95vw)",
        maxHeight: "80vh",
        display: "flex",
        flexDirection: "column",
      }}
    >
      {/* Header */}
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          padding: "14px 20px",
          borderBottom: "1px solid #e5e7eb",
        }}
      >
        <span style={{ fontWeight: 600, fontSize: "1rem" }}>
          Select Checkpoints
        </span>
        <button
          type="button"
          className="linklike"
          style={{ fontSize: "1.2rem", lineHeight: 1 }}
          onClick={onCancel}
          aria-label="Close"
        >
          <i className="bi bi-x-lg" />
        </button>
      </div>
      {/* Toolbar */}
      <div
        style={{
          display: "flex",
          gap: 12,
          padding: "10px 20px",
          borderBottom: "1px solid #e5e7eb",
          alignItems: "center",
        }}
      >
        <span style={{ fontSize: "0.85rem", color: "#6b7280" }}>
          {pending.size} of {devices.length} selected
        </span>
        <button type="button" className="linklike" onClick={onSelectAll}>
          All
        </button>
        <button type="button" className="linklike" onClick={onSelectNone}>
          None
        </button>
      </div>
      {/* Chip list */}
      <div style={{ overflowY: "auto", padding: "14px 20px", flex: 1 }}>
        {devices.length === 0 ? (
          <span style={{ color: "#6b7280", fontSize: "0.9rem" }}>
            No checkpoints of this type.
          </span>
        ) : (
          <div className="chips">
            {devices.map((d) => (
              <button
                key={d.deviceId}
                type="button"
                className={`chip ${pending.has(d.deviceName) ? "active" : ""}`}
                onClick={() => onToggle(d.deviceName)}
              >
                {d.deviceName}
              </button>
            ))}
          </div>
        )}
      </div>
      {/* Footer */}
      <div
        style={{
          display: "flex",
          justifyContent: "flex-end",
          gap: 10,
          padding: "12px 20px",
          borderTop: "1px solid #e5e7eb",
        }}
      >
        <button type="button" className="btn btn-outline" onClick={onCancel}>
          Cancel
        </button>
        <button type="button" className="btn btn-primary" onClick={onSubmit}>
          Apply
        </button>
      </div>
    </div>
  </div>
);

export default Dashboard;
